<script lang="ts">
	/**
	 * Prose skeleton with ragged line ends and, optionally, word-sized chunks
	 * inside each line.
	 *
	 * The irregularity is the point — evenly spaced full-width bars read as a
	 * loading *widget*, whereas broken lines of varying width read as text that
	 * has not arrived yet. Use for descriptions, notes and generated copy.
	 */

	/** Width cap, matching Flowbite's placeholder sizes. */
	export let size: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' = 'sm';

	/** How many lines to draw, 1–6. */
	export let count: number = 6;

	/**
	 * Bar thickness as a Tailwind class. Passed whole rather than as a number,
	 * because Tailwind's scanner cannot see classes assembled at runtime.
	 */
	export let heightClass: string = 'h-2.5';

	/** Break each line into word-shaped chunks. Turn off for a plainer look. */
	export let smallChunks: boolean = true;

	export let divClass: string = 'space-y-2.5 animate-pulse';

	let className: string = '';
	export { className as class };

	const sizes: Record<string, string> = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
		xxl: 'max-w-2xl'
	};

	// Two tones so chunks within a line stay distinguishable.
	const barLight = 'bg-gray-300 rounded-full dark:bg-gray-700';
	const barDark = 'bg-gray-400 rounded-full dark:bg-gray-600';
</script>

<div role="status" class="{sizes[size]} {divClass} {className}">
	<div class="flex items-center space-x-2 w-full">
		{#if smallChunks}
			<div class="{heightClass} {barLight} w-32" />
			<div class="{heightClass} {barDark} w-24" />
		{/if}
		<div class="{heightClass} {barDark} w-full" />
	</div>
	{#if count > 1}
		<div class="flex items-center space-x-2 w-11/12">
			<div class="{heightClass} {barLight} w-full" />
			{#if smallChunks}
				<div class="{heightClass} {barDark} w-full" />
				<div class="{heightClass} {barDark} w-24" />
			{/if}
		</div>
	{/if}
	{#if count > 2}
		<div class="flex items-center space-x-2 w-9/12">
			<div class="{heightClass} {barDark} w-full" />
			{#if smallChunks}
				<div class="{heightClass} {barLight} w-80" />
				<div class="{heightClass} {barDark} w-full" />
			{/if}
		</div>
	{/if}
	{#if count > 3}
		<div class="flex items-center space-x-2 w-11/12">
			<div class="{heightClass} {barLight} w-full" />
			{#if smallChunks}
				<div class="{heightClass} {barDark} w-full" />
				<div class="{heightClass} {barDark} w-24" />
			{/if}
		</div>
	{/if}
	{#if count > 4}
		<div class="flex items-center space-x-2 w-10/12">
			<div class="{heightClass} {barDark} w-32" />
			<div class="{heightClass} {barDark} w-24" />
			<div class="{heightClass} {barLight} w-full" />
		</div>
	{/if}
	{#if count > 5}
		<div class="flex items-center space-x-2 w-8/12">
			<div class="{heightClass} {barDark} w-full" />
			{#if smallChunks}
				<div class="{heightClass} {barLight} w-80" />
				<div class="{heightClass} {barDark} w-full" />
			{/if}
		</div>
	{/if}
	<span class="sr-only">Loading...</span>
</div>
